import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { getUserInfo } from '@/lib/api/user';
import { useDeviceDetect } from '@/hooks/useDeviceDetect';

interface ToAdminFixedBtnProps {
  className?: string;
}

// 配置需要显示按钮的路由路径
const VISIBLE_ROUTES = [
  '/ai-training/list',
    '/ai-supervisor/list'
  // 可以在这里添加更多需要显示按钮的路由
];

const ToAdminFixedBtn: React.FC<ToAdminFixedBtnProps> = ({ className = '' }) => {
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const location = useLocation();
  const { isMobile } = useDeviceDetect();

  /**
   * @description: 是否在钉钉应用
   * @return: Boolean
   */
  const isDingtalk = (): boolean => {
    // 非专有钉
    const isGdt = navigator.userAgent.toLowerCase().match(/TaurusApp/i) !== null;
    return /dingtalk/.test(navigator.userAgent.toLowerCase()) && !isGdt;
  };

  /**
   * @description: 判断当前路由是否需要显示按钮
   * @return: Boolean
   */
  const shouldShowButton = (): boolean => {
    // 检查当前路径是否在配置的可见路由列表中
    return VISIBLE_ROUTES.some(route => location.pathname.startsWith(route));
  };

  // 检查用户是否为管理员
  const checkUserAdmin = async () => {
    try {
      const response = await getUserInfo();
      if (response.success && response.data) {
        // 检查 is_admin 字段，如果不存在则检查 is_superuser 字段
        const adminStatus = response.data.is_admin ?? response.data.is_superuser ?? false;
        setIsAdmin(adminStatus);
      }
    } catch (error) {
      console.error('获取用户信息失败:', error);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 只有在当前路由需要显示按钮时才调用 getUserInfo
    if (shouldShowButton()) {
      checkUserAdmin();
    } else {
      setLoading(false);
    }
  }, [location.pathname]);

  const handleGoToAdmin = () => {
    // 获取当前域名并跳转到管理员后台
    const currentOrigin = window.location.origin;
    let adminUrl = `${currentOrigin}/admin/`;

    // 如果在钉钉环境下，添加 ddtab=true 参数
    if (isDingtalk()) {
      adminUrl += '?ddtab=true';
    }

    window.open(adminUrl, '_blank');
  };

  // 综合判断是否显示按钮：
  // 1. 不在加载中
  // 2. 是管理员
  // 3. 不是移动端
  // 4. 当前路由在配置的可见路由列表中
  if (loading || !isAdmin || isMobile || !shouldShowButton()) {
    return null;
  }

  return (
    <StyledWrapper className={`fixed z-50 ${className}`}>
      <button
        className="animated-button"
        onClick={handleGoToAdmin}
        data-tour="to-admin-btn"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="arr-2" viewBox="0 0 24 24">
          <path d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z" />
        </svg>
        <span className="text mr-2">To Admin</span>
        <span className="circle" />
        <svg xmlns="http://www.w3.org/2000/svg" className="arr-1" viewBox="0 0 24 24">
          <path d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z" />
        </svg>
      </button>
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  position: fixed;
  top: 48px;
  right: 16px;
  z-index: 9999;

  .animated-button {
    position: relative;
    display: flex;
    align-items: center;
    gap: 3px;
    padding: 4px 16px;
    border: 3px solid;
    border-color: #fff;
    font-size: 12px;
    background-color: rgba(255, 255, 255, 0.7);
    border-radius: 100px;
    font-weight: 600;
    color: rgba(31, 56, 126, 0.7);
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.5);
    cursor: pointer;
    overflow: hidden;
    opacity: 0.6;
    transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  }

  .animated-button svg {
    position: absolute;
    width: 16px;
    fill: rgba(31, 56, 126, 0.7);
    z-index: 9;
    transition: all 0.8s cubic-bezier(0.23, 1, 0.32, 1);
  }

  .animated-button .arr-1 {
    right: 8px;
  }

  .animated-button .arr-2 {
    left: -25%;
  }

  .animated-button .circle {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 20px;
    height: 20px;
    background-color: linear-gradient(90deg, #c5e5e4 0%, #a2d9d8 100%);
    border-radius: 50%;
    opacity: 0;
    transition: all 0.8s cubic-bezier(0.23, 1, 0.32, 1);
  }

  .animated-button .text {
    position: relative;
    z-index: 1;
    transform: translateX(-6px);
    transition: all 0.8s cubic-bezier(0.23, 1, 0.32, 1);
  }

  .animated-button:hover {
    box-shadow: 0 0 0 8px transparent;
    color: #212121;
    border-radius: 10px;
    opacity: 1;
    background-color: #ffffff;
  }

  .animated-button:hover .arr-1 {
    right: -25%;
  }

  .animated-button:hover .arr-2 {
    left: 8px;
  }

  .animated-button:hover .text {
    transform: translateX(6px);
  }

  .animated-button:hover svg {
    fill: #1f387e;
  }

  .animated-button:active {
    scale: 0.95;
    box-shadow: 0 0 0 4px #4CAF50;
  }

  .animated-button:hover .circle {
    width: 120px;
    height: 120px;
    opacity: 1;
  }
`;

export default ToAdminFixedBtn;
